import { useState } from 'react';
import { PageHeader } from '@/components/layout/PageHeader';
import { ProfileTable } from '../components/ProfileTable';
import { Button } from '@/components/ui/button';
import { Link } from '@tanstack/react-router';
import { Plus, Download, RefreshCw } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import ProfilesService from '../services/profiles-service';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';

export function ProfilesPage() {
  const [positionTypeFilter, setPositionTypeFilter] = useState<string | undefined>(undefined);
  const [showActiveFilter, setShowActiveFilter] = useState<boolean | undefined>(undefined);
  
  // Fetch profiles using React Query
  const { 
    data: profiles, 
    isLoading, 
    isError, 
    error, 
    refetch 
  } = useQuery({
    queryKey: ['profiles', positionTypeFilter, showActiveFilter],
    queryFn: () => ProfilesService.getProfiles(positionTypeFilter, showActiveFilter)
  });

  return (
    <>
      <PageHeader
        title="Profiles"
        description="Manage executive team and board member profiles for the kiosk display."
        actions={
          <>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh
            </Button>
            <Button variant="outline" size="sm">
              <Download className="mr-2 h-4 w-4" />
              Export
            </Button>
            <Button asChild size="sm">
              <Link to="/profiles/add">
                <Plus className="mr-2 h-4 w-4" />
                Add Profile
              </Link>
            </Button>
          </>
        }
      />
      
      <div className="space-y-4">
        {isError && (
          <Alert variant="destructive">
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>
              {error instanceof Error ? error.message : 'Failed to load profiles'}
            </AlertDescription>
          </Alert>
        )}
        
        {isLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-[500px] w-full" />
          </div>
        ) : (
          <ProfileTable 
            data={profiles || []} 
            onPositionTypeFilterChange={setPositionTypeFilter}
            onShowActiveFilterChange={setShowActiveFilter}
          />
        )}
      </div>
    </>
  );
}

export default ProfilesPage;
